import { useState } from 'react';
import { Plus } from 'lucide-react';
import {
  useAnnouncementsQuery,
  useCreateAnnouncementMutation,
  useUpdateAnnouncementMutation,
  useRetranslateAnnouncementMutation,
  useDeleteAnnouncementMutation,
} from 'hooks/useAnnouncements';
import type { Announcement, AnnouncementInput } from 'types/announcements';
import { AnnouncementList } from 'components/announcements/AnnouncementList';
import { AnnouncementForm } from 'components/announcements/AnnouncementForm';

export function AnnouncementsPage() {
  const { data: announcements = [], isLoading } = useAnnouncementsQuery();
  const createMut = useCreateAnnouncementMutation();
  const updateMut = useUpdateAnnouncementMutation();
  const retranslateMut = useRetranslateAnnouncementMutation();
  const deleteMut = useDeleteAnnouncementMutation();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Announcement | null>(null);

  const handleSave = async (input: AnnouncementInput) => {
    if (editing) {
      return updateMut.mutateAsync({ id: editing.id, patch: input });
    }
    return createMut.mutateAsync(input);
  };

  const handleClose = () => {
    setFormOpen(false);
    setEditing(null);
  };

  return (
    <div className="h-[calc(100vh-96px)] overflow-y-auto bg-gray-50 p-6">
      <header className="mb-6 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-950">Kampanjer</h1>
          <p className="mt-1 text-sm text-gray-500">
            Banners som visas ovanför chattsvar under en kampanjperiod.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          className="inline-flex items-center gap-1.5 rounded-lg bg-gray-950 px-3 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
        >
          <Plus className="h-4 w-4" />
          Ny kampanj
        </button>
      </header>

      {isLoading ? (
        <div className="text-sm text-gray-500">Laddar kampanjer…</div>
      ) : announcements.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-400">
          Inga kampanjer ännu. Skapa en för att börja.
        </div>
      ) : (
        <AnnouncementList
          announcements={announcements}
          onSelect={(a) => {
            setEditing(a);
            setFormOpen(true);
          }}
        />
      )}

      {formOpen && (
        <AnnouncementForm
          existing={editing ?? undefined}
          onSave={handleSave}
          onRetranslate={
            editing ? () => retranslateMut.mutate(editing.id) : undefined
          }
          onDelete={
            editing
              ? () => {
                  deleteMut.mutate(editing.id);
                  handleClose();
                }
              : undefined
          }
          onClose={handleClose}
          saving={createMut.isPending || updateMut.isPending}
        />
      )}
    </div>
  );
}

export default AnnouncementsPage;
